/* @flow */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

const rootLevelComponents = [];

function styleToObject(style) {
  if (!style) {
    return {};
  }

  return style
    .trim()
    .split(';')
    .reduce((result, rule) => {
      if (!rule || !rule.length) {
        return result;
      }

      const tokens = rule.split(':');
      result[tokens[0].trim()] = tokens[1];

      return result;
    }, {});
}

function styleToString(style) {
  return Object.keys(style)
    .filter(property => style[property])
    .map(property => {
      return `${property}: ${style[property]};`;
    })
    .join(' ');
}

function setStyle(elm, style) {
  const newStyle = styleToString({
    ...styleToObject(elm.getAttribute('style')),
    ...style,
  });

  if (newStyle.length) {
    elm.setAttribute('style', newStyle);
  } else {
    elm.removeAttribute('style');
  }
}

function setBodyScroll(scroll) {
  const body = withScrollContext.getBody();
  const html = withScrollContext.getHTML();

  if (!body || !html) {
    return;
  }

  if (!scroll) {
    setStyle(body, {
      overflow: 'hidden',
      position: 'fixed',
      width: '100%',
      top: -Math.max(html.scrollTop, body.scrollTop) + 'px',
      left: -Math.max(html.scrollLeft, body.scrollLeft) + 'px',
    });
    setStyle(html, {
      overflow: 'hidden',
      position: 'fixed',
      width: '100%',
    });
  } else {
    const bodyStyle = styleToObject(body.getAttribute('style'));

    const top = parseInt(bodyStyle.top.replace('px'), 10) * -1;
    const left = parseInt(bodyStyle.left.replace('px'), 10) * -1;

    setStyle(body, {
      overflow: null,
      position: null,
      width: null,
      top: null,
      left: null,
    });
    setStyle(html, {
      overflow: null,
      position: null,
      width: null,
    });

    withScrollContext.getWindow().scrollTo(left, top);
  }
}

export default function withScrollContext(WrappedComponent: any) {
  class ScrollContext extends Component<any, any, any> {
    props: any;
    state = {
      scroll: true,
    };
    getChildContext() {
      return {
        scroll: this.state.scroll,
        setScroll: this.setScroll,
      };
    }
    componentDidMount() {
      if (this.context.setScroll) {
        this.context.setScroll(false);
      } else {
        rootLevelComponents.push(this);
        setBodyScroll(false);
      }
    }
    componentWillUnmount() {
      if (this.context.setScroll) {
        this.context.setScroll(true);
      } else {
        rootLevelComponents.push(this);
        setBodyScroll(true);
      }
    }
    setScroll = (scroll: boolean) => {
      this.setState({ scroll });
    };
    render() {
      return <WrappedComponent {...this.props} scroll={this.state.scroll} />;
    }
  }

  ScrollContext.displayName =
    WrappedComponent.displayName || WrappedComponent.name;

  ScrollContext.contextTypes = {
    setScroll: PropTypes.func,
  };

  ScrollContext.childContextTypes = {
    scroll: PropTypes.bool,
    setScroll: PropTypes.func,
  };

  return ScrollContext;
}

/* istanbul ignore next */
withScrollContext.getBody = () => {
  return document.body;
};

/* istanbul ignore next */
withScrollContext.getHTML = () => {
  return document.documentElement;
};

/* istanbul ignore next */
withScrollContext.getWindow = () => {
  return window;
};
